import { useState, useEffect } from 'react';

const STORAGE_KEY = 'mandapost_coming_soon_dismissed';

export default function ComingSoonModal() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSubmitted(true);
    setTimeout(dismiss, 2500);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_0.3s_ease-out]">
        {/* Top gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#22577A] via-[#38a3a5] to-[#57cc99]" />

        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="px-8 py-8">
          {/* Logo */}
          <div className="flex justify-center mb-5">
            <img src="/assets/MandaPost_Logo.svg" alt="MandaPost" className="h-9 w-auto" />
          </div>

          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <span className="inline-block bg-[#e4f4ff] text-[#22577A] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                  Coming Soon
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Be the First to Know!
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  MandaPost is launching soon. Drop your email below and we'll let you know the moment we go live — plus get exclusive early-access perks.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#22577A]/40 focus:border-[#22577A] text-base transition-all"
                  />
                  {error && <p className="mt-1.5 text-red-500 text-xs">{error}</p>}
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#22577A] hover:bg-[#1a4460] text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm cursor-pointer"
                >
                  Notify Me at Launch
                </button>
              </form>

              <p className="text-center text-xs text-gray-400 mt-4">
                No spam, ever. Unsubscribe at any time.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 bg-[#e4f4ff] rounded-full flex items-center justify-center">
                  <svg className="h-7 w-7 text-[#22577A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">You're on the list!</h2>
              <p className="text-gray-500 text-sm">
                We'll send you a note the moment MandaPost launches. Stay tuned!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
